import callCmAPI from 'services/helpers/call-cm-api'
import {
  AllowCreditPolicyT,
  CustomerCreditPolicyT,
} from 'types/customer-restrictions'
import { shrinkAllowCreditPolicy } from 'utils/transform-legacy-rights'

// One row of RisikaAPI's restriction_templates, as returned by
// GET /customer/plans (CMAPI forwards api_auth's /company/plans verbatim).
//
// `rights` is the template's JSONB: per-country `score_models` (or a single
// `place_holder_country` entry for plans that follow the customer's own
// country, e.g. MANUAL_PACKAGE / FREEMIUM) plus the `global` flags. Templates
// only carry the global flags they set explicitly; RisikaAPI's
// refresh_restrictions fills the missing ones with false.
export type TemplateScoreModel = { model: string; explanation?: boolean }
export type TemplateCountryRights = { score_models?: TemplateScoreModel[] }
export type TemplateGlobalRights = Record<string, boolean | null | undefined>
// Keyed by country code, `place_holder_country` or `global`.
export type TemplateRights = Record<
  string,
  TemplateCountryRights | TemplateGlobalRights | undefined
>

export const templateGlobalFlags = (
  rights: TemplateRights | undefined,
): TemplateGlobalRights => (rights?.global ?? {}) as TemplateGlobalRights

export const templateCountryModels = (
  rights: TemplateRights | undefined,
  country: string,
): TemplateScoreModel[] | undefined =>
  (rights?.[country] as TemplateCountryRights | undefined)?.score_models

export type RawPlan = {
  name: string
  tier: number
  rights: TemplateRights
  allow_api: boolean
  // Risika for AI (ENG2-2542). Optional: templates only carry it once
  // RisikaAPI ENG2-2540 is deployed; mapPlanTemplate reads a missing one as
  // false.
  allow_ai?: boolean
  allow_basic: boolean
  allow_browser_extension: boolean
  allow_company_export: boolean
  allow_company_search: boolean
  allow_countries: string[]
  allow_credit: boolean
  allow_credit_policy: AllowCreditPolicyT
  allow_ean: boolean
  allow_financial: boolean
  allow_hierarchy: boolean
  allow_highlights: boolean
  allow_monitoring: boolean
  allow_monitoring_export: boolean
  allow_notes: boolean
  allow_pdf_reports: boolean
  allow_person_export: boolean
  allow_person_search: boolean
  allow_relations: boolean
  // Sales Signals (ENG2-2815). Optional like allow_ai: templates only carry it
  // once RisikaAPI #3184 is deployed; mapPlanTemplate reads a missing one as
  // false.
  allow_sales_triggers?: boolean
  allow_webhook: boolean
  allow_zones: Record<string, string[]> | null
  number_of_users: number
  number_of_creditchecks: number
  number_of_credit_ratings: number
  number_of_credit_reports: number
  number_of_monitoring: number
  number_of_monitoring_exports: number
  number_of_search_exports: number
  number_of_global_lookups: Record<string, number> | null
  search_exports_limit: number
  global_lookups_limit: Record<string, number> | null
}

// The counters keep the customer-side names (`lookups`, `max_users`, …) so
// they line up with CustomerRestrictionsResponse.caps; everything else keeps
// the template column name. ENG2-2455 (T4): the whole row is carried so the
// Restore Defaults dialog can compare every right against the template.
export type DefaultPlan = {
  name: string
  max_users: number
  allow_countries: string[]
  allow_credit_policy: CustomerCreditPolicyT
  credit_ratings: number
  credit_reports: number
  lookups: number
  monitoring_count: number
  monitoring_exports: number
  search_exports: number
  tier: number
  search_exports_limit: number
  rights: TemplateRights
  allow_api: boolean
  allow_ai: boolean
  allow_basic: boolean
  allow_browser_extension: boolean
  allow_company_export: boolean
  allow_company_search: boolean
  allow_credit: boolean
  allow_ean: boolean
  allow_financial: boolean
  allow_hierarchy: boolean
  allow_highlights: boolean
  allow_monitoring: boolean
  allow_monitoring_export: boolean
  allow_notes: boolean
  allow_pdf_reports: boolean
  allow_person_export: boolean
  allow_person_search: boolean
  allow_relations: boolean
  allow_sales_triggers: boolean
  allow_webhook: boolean
  allow_zones: Record<string, string[]> | null
  global_lookups_limit: Record<string, number> | null
  number_of_global_lookups: Record<string, number> | null
}

export const mapPlanTemplate = (plan: RawPlan): DefaultPlan => {
  const {
    number_of_users,
    allow_countries,
    allow_credit_policy,
    number_of_credit_ratings,
    number_of_credit_reports,
    number_of_creditchecks,
    number_of_monitoring,
    number_of_monitoring_exports,
    number_of_search_exports,
    allow_ai,
    allow_sales_triggers,
    ...rest
  } = plan

  return {
    ...rest,
    allow_ai: allow_ai ?? false,
    allow_sales_triggers: allow_sales_triggers ?? false,
    max_users: number_of_users,
    allow_countries: (allow_countries ?? []).map((country) =>
      country.toUpperCase(),
    ),
    allow_credit_policy: shrinkAllowCreditPolicy(allow_credit_policy),
    credit_ratings: number_of_credit_ratings,
    credit_reports: number_of_credit_reports,
    lookups: number_of_creditchecks,
    monitoring_count: number_of_monitoring,
    monitoring_exports: number_of_monitoring_exports,
    search_exports: number_of_search_exports,
  }
}

export const getDefaultPlans = (): Promise<DefaultPlan[]> =>
  callCmAPI(`/customer/plans`, {
    method: 'get',
  })
    .then(({ data: { data } }) => data.map(mapPlanTemplate))
    .catch((err) => err)
